import { useState } from "react";
import { LuArrowLeft } from "react-icons/lu";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import {
  isTelegramAppAuth,
  linkTelegramAppAfterAuth,
} from "../../lib/telegramApp";
import { isValidPhone } from "../../lib/profiles";
import { AuthAlert, AuthShell, Field } from "../../components/auth/AuthShell";
import { Loader, Spinner } from "../../components/ui/Loader";

export const Signup = () => {
  const { user, loading, signUp } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const telegramAuth = isTelegramAppAuth(location.search);

  if (loading) {
    return <Loader />;
  }

  if (user && !telegramAuth) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleFirstStep = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!email.trim()) {
      setError("Вкажіть email");
      return;
    }
    if (password.length < 7) {
      setError("Пароль має містити мінімум 7 символів");
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!name.trim()) {
      setError("Вкажіть імʼя");
      return;
    }
    if (!company.trim()) {
      setError("Вкажіть компанію або архітектурне бюро");
      return;
    }
    if (!isValidPhone(phone)) {
      setError("Вкажіть коректний номер телефону");
      return;
    }
    setSubmitting(true);
    try {
      const result = await signUp(name.trim(), email.trim(), password, {
        company: company.trim(),
        phone: phone.trim(),
      });
      if (result.error) {
        setError(result.error);
      } else if (result.needsEmailConfirmation) {
        setInfo("Перевірте пошту та підтвердіть акаунт, щоб увійти.");
      } else {
        await linkTelegramAppAfterAuth(location.search).catch(() => {});
        navigate("/profile", { replace: true });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не вдалося зареєструватися");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Реєстрація партнера"
      subtitle="Створіть акаунт, щоб передавати проєкти"
    >
      {step === 1 ? (
        <form onSubmit={handleFirstStep} className="mt-7 flex flex-col gap-4">
          <Field
            label="Email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
          <Field
            label="Пароль"
            type="password"
            placeholder="Мінімум 7 символів"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={7}
            required
          />
          {error && <AuthAlert kind="error" message={error} />}
          {info && <AuthAlert kind="info" message={info} />}
          <button
            type="submit"
            className="btn-brand mt-2 w-full justify-center"
          >
            Далі
          </button>
        </form>
      ) : (
        <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4">
          <Field
            label="Повне імʼя"
            type="text"
            placeholder="Імʼя та прізвище"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            required
          />
          <Field
            label="Компанія / бюро"
            type="text"
            placeholder="Назва компанії"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            autoComplete="organization"
            required
          />
          <Field
            label="Телефон"
            type="tel"
            placeholder="+380..."
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
            required
          />
          {error && <AuthAlert kind="error" message={error} />}
          {info && <AuthAlert kind="info" message={info} />}
          <button
            type="button"
            onClick={() => {
              setStep(1);
              setError(null);
            }}
            className="link-brand gap-1 self-start text-xs"
          >
            <LuArrowLeft aria-hidden />Назад
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn-brand mt-2 w-full justify-center disabled:opacity-70"
          >
            {submitting ? (
              <>
                <Spinner className="h-4 w-4" />
                Зачекайте…
              </>
            ) : (
              "Зареєструватися"
            )}
          </button>
        </form>
      )}
    </AuthShell>
  );
};
